package com.nnod.ui.theme

import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.ui.graphics.Color

// Nnod brand palette — approximated from the oklch design tokens in App Prototype.html
val NnodPurpleDeep  = Color(0xFF5B21B6)   // oklch(48% 0.22 295) — primary CTA
val NnodPurpleMid   = Color(0xFF8B5CF6)   // oklch(65% 0.18 295) — secondary accent
val NnodPurpleLight = Color(0xFFC4B5FD)   // oklch(82% 0.10 295) — container / highlights
val NnodGlassBg     = Color(0xC7F8F5FF)   // rgba(248,245,255, 0.78) — glass card surface
val NnodGlassBorder = Color(0x59B496E6)   // rgba(180,150,230, 0.35) — glass card border
val NnodTextDark    = Color(0xFF2E1065)   // oklch(30% 0.12 295) — primary text
val NnodTextMid     = Color(0xFF6D5B9E)   // oklch(52% 0.10 295) — secondary text
val NnodTextLight   = Color(0xFF9B8BB8)   // oklch(70% 0.08 295) — hint / caption text
val NnodBgStart     = Color(0xFFEDE8FA)   // gradient start (#ede8fa)
val NnodBgEnd       = Color(0xFFDDF0FC)   // gradient end   (#ddf0fc)

// Artist row
val NnodArtistAddFill  = Color(0x1AB496E6)   // "add" circle fill
val NnodArtistBorder   = Color(0x80A078DC)   // avatar / add-button circle border
val NnodArtistFill     = Color(0xFFB496E6)   // avatar placeholder fill (no photo)

// Banner card
val NnodBannerGradStart = Color(0xFF3B1FA8)  // hero banner gradient — dark end
val NnodBannerGradEnd   = Color(0xFF7C3AED)  // hero banner gradient — light end
val NnodBannerScrim     = Color(0x55000000)  // translucent scrim over banner image

// Gacha card
val NnodGachaGradStart  = Color(0xFF4C1D95)  // gacha card gradient — deep indigo
val NnodGachaGradEnd    = Color(0xFFA78BFA)  // gacha card gradient — lavender

// On-image overlays
val NnodGlassWhite      = Color(0x26FFFFFF)  // badge / chip surface (15% white)
val NnodGlassWhiteFaint = Color(0x1AFFFFFF)  // decorative overlay (10% white)

// On-image text
val NnodOnImagePrimary  = Color(0xA6FFFFFF)  // badge label — 65% white
val NnodOnImageMid      = Color(0x99FFFFFF)  // secondary label — 60% white
val NnodOnImageSub      = Color(0xB3FFFFFF)  // subtitle — 70% white
val NnodOnImageLow      = Color(0x59FFFFFF)  // inactive indicator — 35% white

// Input / form field
val NnodInputSurface    = Color(0xFFFFFFFF)  // pure white elevated surface for text inputs

// Announcement status badge — scheduled state
val NnodAmberSurface          = Color(0xFFFEF3CC)   // oklch(95% 0.05 70) — scheduled badge bg
val NnodAmberText             = Color(0xFF7A5000)   // oklch(50% 0.12 70) — scheduled badge text

// Announcement type badge — 新機能 (new-feature) variant
val NnodFeatureBadgeGradStart = Color(0xFF0EA5E9)   // sky-blue gradient start
val NnodFeatureBadgeGradEnd   = Color(0xFF7DD3FC)   // sky-blue gradient end

// Modal / dialog surface
val NnodDialogSurface         = Color(0xFFFDFBFF)   // near-white surface for overlay dialogs

// Danger / destructive action
val NnodDangerText            = Color(0xFFB91C1C)   // red text for delete / destructive buttons
val NnodBatteryLow            = Color(0xFFE53935)   // subscription battery: low
val NnodBatteryMedium         = Color(0xFFFB8C00)   // subscription battery: medium
val NnodBatteryHigh           = Color(0xFF26A69A)   // subscription battery: high

// Nod-IN URL page — status badges
val NnodStatusActiveBg        = Color(0x406EBF78)   // oklch(80% 0.12 145 / 0.25) — live badge bg
val NnodStatusActiveText      = Color(0xFF166534)   // oklch(40% 0.18 145)        — live badge text

// Nod-IN URL page — end-session button gradient (warm red-orange, oklch hue 25)
val NnodEndSessionGradStart   = Color(0xFFBF360C)   // oklch(55% 0.20 25)
val NnodEndSessionGradEnd     = Color(0xFFE64A19)   // oklch(65% 0.18 25)

// Nod-IN URL page — help panel
val NnodHelpPanelBgStart      = Color(0x99EDE8FF)   // oklch(94% 0.06 295 / 0.6)
val NnodHelpPanelBgEnd        = Color(0x80E8F4FF)   // oklch(95% 0.04 240 / 0.5)
val NnodHelpPanelBorder       = Color(0x59A07AE0)   // oklch(72% 0.16 295 / 0.35)

// Nod-IN URL page — URL display box
val NnodUrlBoxBg              = Color(0x99F5F0FF)   // oklch(96% 0.03 295 / 0.6)

// Nod-IN landing — End kind warm background
val NnodNodinEndBgFrom      = Color(0xFFF5EBE5)   // oklch(94% 0.06 30) warm salmon
val NnodNodinEndBgTo        = Color(0xFFF2EEE0)   // oklch(94% 0.04 70) warm cream

// Nod-IN landing — End kind status badge
val NnodNodinEndStatusBg    = Color(0x40F07A50)   // oklch(80% 0.14 30 / 0.25)
val NnodNodinEndStatusFg    = Color(0xFF8B2000)   // oklch(40% 0.2 30)
val NnodNodinEndStatusDot   = Color(0xFFCC3A10)   // oklch(60% 0.22 30)

// Nod-IN landing — End kind accent
val NnodNodinEndAccent      = Color(0xFFDC2626)   // #dc2626
val NnodNodinEndAccentSoft  = Color(0xFFFB923C)   // #fb923c
val NnodNodinEndAccentDeep  = Color(0xFF7C1D1D)   // #7c1d1d

// Nod-IN landing — Start kind status dot (badge bg/fg reuse NnodStatusActiveBg/Text)
val NnodNodinStartStatusDot = Color(0xFF3D9950)   // oklch(58% 0.2 145)

// Nod-IN landing — card surface and dividers
val NnodLandingCardBg       = Color(0xD9FFFFFF)   // rgba(255,255,255,0.85)
val NnodLandingCardBorder   = Color(0xE6FFFFFF)   // rgba(255,255,255,0.9)
val NnodLandingDivider      = Color(0x66B0A0D8)   // oklch(80% 0.07 295 / 0.4)

// Nod-IN landing — idol pill surface
val NnodIdolPillBg          = Color(0xB3FFFFFF)   // rgba(255,255,255,0.7)
val NnodIdolPillBorder      = Color(0xE6FFFFFF)   // rgba(255,255,255,0.9)

// Nod-IN landing — instructions panel
val NnodLandingInstructionsBg     = Color(0x8CFFFFFF)   // rgba(255,255,255,0.55) panel bg
val NnodLandingInstructionsBorder = Color(0xD9FFFFFF)   // rgba(255,255,255,0.85) panel border

// Nod-IN app screens — QR scanner viewfinder
val NnodQrScannerBgFrom        = Color(0xFF281949)   // oklch(22% 0.10 295) — very dark purple
val NnodQrScannerBgTo          = Color(0xFF321E64)   // oklch(30% 0.14 280) — dark purple

// Nod-IN app screens — code-joined success banner (green)
val NnodNodinSuccessBgFrom     = Color(0x596EBF78)   // oklch(80% 0.12 145 / 0.35)
val NnodNodinSuccessBgTo       = Color(0x408BCBA7)   // oklch(85% 0.10 165 / 0.25)
val NnodNodinSuccessBorder     = Color(0x6650A557)   // oklch(72% 0.16 145 / 0.40)
val NnodNodinSuccessText       = Color(0xFF14521C)   // oklch(30% 0.18 145) — dark forest green
val NnodNodinSuccessSubtext    = Color(0xFF327639)   // oklch(45% 0.12 145) — medium green

// Nod-IN app screens — warning banner (amber)
val NnodNodinWarningBgFrom     = Color(0x80FFECB3)   // oklch(94% 0.08  50 / 0.50)
val NnodNodinWarningBgTo       = Color(0x66FFF3E0)   // oklch(95% 0.05  30 / 0.40)
val NnodNodinWarningBorder     = Color(0x66D4A017)   // oklch(80% 0.14  60 / 0.40)
val NnodNodinWarningText       = Color(0xFF6B4A00)   // oklch(35% 0.12  50)
val NnodNodinWarningStrong     = Color(0xFF7B3400)   // oklch(40% 0.20  30)

// Nod-IN app screens — event detail meta-grid background
val NnodNodinEventMetaBg       = Color(0x99F1EEFF)   // oklch(96% 0.03 295 / 0.60)

val NnodLightColorScheme = lightColorScheme(
    primary              = NnodPurpleDeep,
    onPrimary            = Color.White,
    primaryContainer     = NnodPurpleLight,
    onPrimaryContainer   = NnodTextDark,
    secondary            = NnodPurpleMid,
    onSecondary          = Color.White,
    secondaryContainer   = Color(0xFFEDE9FF),
    onSecondaryContainer = NnodTextDark,
    tertiary             = Color(0xFF0284C7),
    onTertiary           = Color.White,
    background           = NnodBgStart,
    onBackground         = NnodTextDark,
    surface              = Color(0xFFF8F5FF),
    onSurface            = NnodTextDark,
    surfaceVariant       = NnodBgStart,
    onSurfaceVariant     = NnodTextMid,
    outline              = NnodGlassBorder,
    surfaceContainerLowest = NnodInputSurface,
    error                = Color(0xFFE53935),
    onError              = Color.White,
)

val NnodDarkColorScheme = darkColorScheme(
    primary              = NnodPurpleLight,
    onPrimary            = NnodTextDark,
    primaryContainer     = NnodPurpleDeep,
    onPrimaryContainer   = Color.White,
    secondary            = NnodPurpleMid,
    onSecondary          = Color.White,
    background           = Color(0xFF12091E),
    onBackground         = Color(0xFFEDE8FA),
    surface              = Color(0xFF1E1033),
    onSurface            = Color(0xFFEDE8FA),
    error                = Color(0xFFCF6679),
    onError              = Color.Black,
)
